"use client";
// import { Input } from "@heroui/input";
import { Checkbox } from "@heroui/checkbox";
import React, { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { ErrorMap } from "@/types/index";
import { Link, useRouter } from "@/i18n/routing";
import { useModal } from "@/components/modal-context";
import FailModal from "@/components/modals/fail-modal";
import Image from "next/image";
import { Button, PasswordInput, Input } from "@/components/custom";
import { useSearchParams } from "next/navigation";
import _ from "lodash";
import clsx from "clsx";
import { RegisterAction } from "@/lib/actions";
import { useTranslations } from "next-intl";
import { PREFIX_PHONE_NUMBER } from "@/lib/constants";
import { InputCheck, OnAuthenticated } from "@/lib/helper";
import { useAtomValue } from "jotai";
import { ipAddressAtom } from "@/components/_store";
import { useFirebase } from "@/components/firebase-context";

export default function RegisterPage() {
  const { openModal } = useModal();
  const searchParams = useSearchParams();
  const router = useRouter();
  const t = useTranslations();
  const { pushEvent } = useFirebase();

  const ipAddress = useAtomValue(ipAddressAtom);

  const [username, setUsername] = useState<string>("");

  useEffect(() => {
    const referral = searchParams.get("referral");
    pushEvent("register_page_view", {
      referral_code: referral || "none",
    });
    // if (window.dataLayer) {
    //   window.dataLayer.push({
    //     event: "register_page_view",
    //     referral_code: referral || "none",
    //   });
    // }
  }, [searchParams]);

  // phone state
  const [handphoneNumber, setHandphoneNumber] = React.useState(PREFIX_PHONE_NUMBER);

  const OnPhoneNumberValueChange = (value: string) => {
    if (/^\+880\d{0,10}$/.test(value)) {
      if (!value.startsWith(PREFIX_PHONE_NUMBER)) value = PREFIX_PHONE_NUMBER;
      setHandphoneNumber(value);
    }
  };

  // email/phone number,password,confirm password,referral code,checkbox
  const [errors, setErrors] = useState<ErrorMap>({});
  const [usernameMalformed, setUsernameMalformed] = useState<boolean>(false);
  const userSchema = z
    .object({
      username: z.string().min(5, t("username-must-be-at-least-5-characters-long")),
      handphone_no: z
        .string()
        .regex(/^\+8801\d{9}$/, t("invalid-phone-number"))
        .min(14, t("phone-number-must-be-at-least-10-digits-long")),
      password: z.string().min(8, t("password-must-be-at-least-8-characters-long")),
      confirm_password: z.string().min(8, t("password-must-be-at-least-8-characters-long")),
      referral: z.string().optional(),
      checkbox: z.string().refine((val) => val === "true", {
        message: t("please-agree-with-the-terms-and-conditions"),
      }),
    })
    .refine((data) => data.password === data.confirm_password, {
      message: t("passwords-do-not-match"),
      path: ["confirm_password"],
    });

  const formHandler = useCallback(
    async (formData: FormData) => {
      formData.set("checkbox", formData.get("checkbox") === null ? "false" : "true");
      const safeParse = userSchema.safeParse(Object.fromEntries(formData.entries()));
      if (!safeParse.success) {
        // convert zod res into error map {fieldName:error}
        const errorMap = safeParse.error.errors.reduce((acc: ErrorMap, err) => {
          acc[err.path[0] as string] = err.message;
          return acc;
        }, {} as ErrorMap);
        setErrors(errorMap);
        return;
      }
      const res = await RegisterAction(safeParse.data, ipAddress);
      if (res.status_code === 0) {
        // login success
        // openModal(<SuccessRegisterOverlay />, "", { size: "sm" });
        OnAuthenticated(res.access_token);
        if ("hash_code" in res) {
          // const query = {
          //   ...Object.fromEntries(searchParams.entries()),
          // };
          router.push({
            pathname: "/" + res.hash_code,
            query: {
              ...(safeParse.data.referral && { referral: safeParse.data.referral }),
              // ...Object.fromEntries(searchParams.entries()),
              ...(searchParams.get("utm_source") && { utm_source: searchParams.get("utm_source") }),
              ...(searchParams.get("utm_medium") && { utm_medium: searchParams.get("utm_medium") }),
              ...(searchParams.get("utm_campaign") && { utm_campaign: searchParams.get("utm_campaign") }),
            },
          });
        }
      } else {
        // login failed
        console.log(res);
        openModal(<FailModal description={res.message} />, t("failed-to-register"));
      }

      // router.push("/succeed/register");
      // if (window.dataLayer) {
      //   // ((\?|\&)(referral)\=([a-bA-B0-9]))\w+
      //   // window.dataLayer.push({
      //   //   event: "register.success",
      //   //   hasReferral: searchParams.has("E2QP4Y"),
      //   // });
      //   window.dataLayer.push({
      //     event: "register_success",
      //     referral_code: searchParams.has("referral") ? searchParams.get("referral") : "none",
      //   });
      // }
    },
    [ipAddress]
  );
  return (
    <div className="flex w-full flex-col gap-y-4">
      <Image
        src="/banner/auth/register.png"
        className="mx-auto w-full max-w-screen-sm rounded-2xl border-2 border-solid border-transparent bg-gradient-to-r from-[#FFEA92] via-[#C9A04C] to-[#FFFFA5] bg-clip-border shadow-2xl"
        alt="ads"
        width={1500 / 3}
        height={690 / 3}
      />
      <h1 className="text-center text-2xl font-bold">{t("register")}</h1>
      <form id="register" className="flex w-full flex-col gap-y-3" action={formHandler}>
        <Input
          name="username"
          value={username}
          onValueChange={(e) => {
            if (!errors.username && InputCheck.username.isMalformed(e)) {
              setUsernameMalformed(true);
            }
            setUsername(InputCheck.username.format(e));
          }}
          isInvalid={!!errors.username}
          errorMessage={errors.username}
          onFocus={() => setErrors((prev) => _.omit(prev, "username"))}
          type="text"
          label={t("username")}
          className="w-full"
          variant="bordered"
          description={usernameMalformed && <span className="text-danger">{t("username-malformed")}</span>}
        />
        <Input
          name="handphone_no"
          isInvalid={!!errors.handphone_no}
          errorMessage={errors.handphone_no}
          onFocus={() => setErrors((prev) => _.omit(prev, "handphone_no"))}
          type="text"
          value={handphoneNumber}
          // onChange={handphone_noValueChange}
          onValueChange={OnPhoneNumberValueChange}
          label={t("phone-number")}
          placeholder={t("enter-your-phone-number")}
          className="w-full"
          variant="bordered"
        />

        <PasswordInput
          isInvalid={!!errors.password}
          errorMessage={errors.password}
          onFocus={() => setErrors((prev) => _.omit(prev, "password"))}
          name="password"
          label={t("password")}
        />

        <PasswordInput
          isInvalid={!!errors.confirm_password}
          errorMessage={errors.confirm_password}
          onFocus={() => setErrors((prev) => _.omit(prev, "confirm_password"))}
          name="confirm_password"
          label={t("confirm-password")}
        />

        <Input
          name="referral"
          isInvalid={!!errors.referral}
          errorMessage={errors.referral}
          onFocus={() => setErrors((prev) => _.omit(prev, "referral"))}
          type="text"
          label={t("referral-code")}
          placeholder={t("enter-your-referral-code")}
          classNames={{
            input: clsx({ "text-gray-500": searchParams.get("referral") }),
          }}
          isReadOnly={!!searchParams.get("referral")}
          defaultValue={searchParams.get("referral") || undefined}
        />
        {/* #TODO: need find a way custom the checkbox green */}
        {/* #SOLUTION: https://heroui.org/docs/components/checkbox#custom-styles */}
        <Checkbox
          type="checkbox"
          isInvalid={!!errors.checkbox}
          onFocus={() => setErrors((prev) => _.omit(prev, "checkbox"))}
          name="checkbox"
          color="success"
        >
          <p className="text-sm">
            {/* I am of legal age and I agree with the{" "}
            <a href="#" className="text-[#258966]">
              Term and Conditions
            </a> */}
            {t.rich("register-tnc", {
              a: (chunks) => (
                <Link href="#" target="_blank" className="text-[#258966]">
                  {" "}
                  {chunks}
                </Link>
              ),
            })}
          </p>
        </Checkbox>
        <span className="text-tiny text-danger">{errors.checkbox}</span>
        <Button type="submit" className="mt-5 w-full bg-[#258966] disabled:bg-gray-600">
          {t("register-now")}
        </Button>
        <div className="mt-5 flex flex-col items-center justify-center gap-y-5">
          <div className="flex flex-row gap-x-1 text-sm">
            <p>{t("already-have-an-account")}</p>
            <Link href="/login" className="text-[#258966]">
              {t("sign-in")}
            </Link>
          </div>
        </div>
      </form>
      {/* <Image src="/register/banner8.png" className="w-full" alt="ads" width={300} height={300} /> */}
    </div>
  );
}
